/**
 * The one practitioner <-> owner term map.
 *
 * Five words carry almost all of the vocabulary an owner cannot be expected to know, and each has a
 * mandated synonym. Guided copy uses the owner term. Working shows the practitioner term in
 * parentheses once, because an accountant who is about to open the real panel has to learn the word
 * the panel uses. Workpapers is untouched: a practitioner reads the practitioner's vocabulary.
 *
 * This rewrites WORDING only. It never touches a number, a code, a severity or a count — those come
 * from the engine and are the same in every density.
 */
export type QoeDensity = 'guided' | 'working' | 'workpapers'

export interface QoeOwnerTerm {
  /** The word as a practitioner writes it. */
  practitioner: string
  /** The owner-facing replacement. */
  owner: string
  /**
   * Every inflection, longest first: "normalized EBITDA" has to win over "normalized", or the
   * replacement leaves "adjusted for EBITDA" behind. Each is case-INSENSITIVE, because the same word
   * opens a sentence in one help entry and sits mid-sentence in the next; `matchCase` restores the
   * capital the original carried.
   */
  forms: ReadonlyArray<readonly [RegExp, string]>
}

/**
 * Phrases that NAME something on screen, exempt from the rewrite.
 *
 * The rewriter changes wording, and a label is not wording. "Use Normalized working capital to see
 * which months are Complete" rewritten to "Use Adjusted for working capital…" sends the reader to a
 * card that is not on the page under that name, which is worse than the word it was avoiding.
 * Matched case-sensitively: it is the capitalised form that is the label.
 *
 * Each entry must be a string the product actually prints. Today: the Overview settings heading
 * (`CfoQoePage.tsx`) and the NWC card heading (`CfoQoePage.tsx`, and the same heading in the Excel
 * databook).
 */
export const QOE_PROTECTED_PHRASES: readonly string[] = [
  'Engagement scope & materiality',
  'Normalized working capital',
]

export const QOE_OWNER_TERMS: readonly QoeOwnerTerm[] = [
  {
    practitioner: 'normalize',
    owner: 'adjust for',
    forms: [
      // "normalized X" is an adjective, and "adjusted for X" is not: the general form has to lose to
      // every noun phrase that actually occurs, or the copy reads "monthly adjusted for NWC".
      [/\bnormalized EBITDA\b/gi, 'adjusted earnings'],
      [/\bnormalized working capital\b/gi, 'adjusted working capital'],
      [/\bnormalized NWC\b/gi, 'adjusted working capital'],
      [/\bnormalizations\b/gi, 'adjustments'],
      [/\bnormalization\b/gi, 'adjustment'],
      [/\bnormalized\b/gi, 'adjusted for'],
      [/\bnormalizes\b/gi, 'adjusts for'],
      [/\bnormalize\b/gi, 'adjust for'],
    ],
  },
  {
    practitioner: 'peg',
    owner: 'working capital target',
    forms: [
      [/\bNWC peg\b/gi, 'working capital target'],
      [/\bpegs\b/gi, 'working capital targets'],
      [/\bpeg\b/gi, 'working capital target'],
    ],
  },
  {
    practitioner: 'pro forma',
    owner: "changes that haven't happened for a full year yet",
    forms: [[/\bpro[- ]forma\b/gi, "changes that haven't happened for a full year yet"]],
  },
  {
    practitioner: 'perimeter',
    owner: "what's included",
    // "the entity perimeter" is a noun phrase: replacing only its head leaves "the entity what's
    // included". The whole phrase is what an owner reads, so the whole phrase is what is replaced.
    forms: [
      [/\bthe entity perimeter\b/gi, "what's included"],
      [/\bentity perimeter\b/gi, "what's included"],
      [/\bperimeters\b/gi, "what's included"],
      [/\bperimeter\b/gi, "what's included"],
    ],
  },
  {
    practitioner: 'materiality',
    owner: 'the size that matters here',
    forms: [[/\bmateriality\b/gi, 'the size that matters here']],
  },
]

/** The stand-in a protected label is swapped for while the term rules run. */
const HOLD = (index: number) => `@@qoe-label-${index}@@`

/** Keeps a replacement in the case the original was written in: sentence-initial words stay capitalised. */
const matchCase = (source: string, replacement: string) =>
  /^[A-Z]/.test(source) ? replacement.charAt(0).toUpperCase() + replacement.slice(1) : replacement

/**
 * Rewrites practitioner vocabulary for the given density.
 *
 * `guided` replaces the term outright; `working` appends the practitioner word in parentheses the
 * FIRST time each term appears, so a paragraph that says "working capital target" four times says
 * "(peg)" once; `workpapers` returns the text unchanged.
 */
export function qoeOwnerText(text: string, density: QoeDensity): string {
  const input = String(text ?? '')
  if (density === 'workpapers' || !input) return input
  // Protected labels are lifted out before the term pass and put back after, so no rule can rewrite
  // the name of something on screen. The placeholder is deliberately ASCII and deliberately ugly: it
  // cannot occur in authored copy and no term pattern matches it, so it cannot itself be rewritten,
  // and unlike a control character it leaves this file readable to grep, diff and every other tool.
  const held: string[] = []
  let out = input
  for (const phrase of QOE_PROTECTED_PHRASES) {
    if (!out.includes(phrase)) continue
    out = out.split(phrase).join(HOLD(held.length))
    held.push(phrase)
  }
  for (const term of QOE_OWNER_TERMS) {
    let introduced = false
    for (const [pattern, owner] of term.forms) {
      out = out.replace(pattern, match => {
        const replacement = matchCase(match, owner)
        if (density === 'guided' || introduced) return replacement
        introduced = true
        return `${replacement} (${term.practitioner})`
      })
    }
  }
  return held.reduce((text, phrase, i) => text.split(HOLD(i)).join(phrase), out)
}

/** The terms an owner should never meet in guided copy. Used by the guide's own copy test. */
export const QOE_BANNED_OWNER_WORDS: readonly string[] = QOE_OWNER_TERMS.map(term => term.practitioner)
